import React from 'react'
import { useState, useEffect, useRef } from 'react'
import { Loading, RenderWhenVisible } from '@marketing-milk/frontend'
import { AdCreative } from '@marketing-milk/interfaces'

import { CreativeCard } from 'pages/business/creatives/CreativeCard/CreativeCard'
import { CreativeListRow } from 'pages/business/creatives/CreativeCard/CreativeListRow'
import { fuzzySearch } from 'components/DataTable/helpers/search.helpers'

import { Alert } from '@material-ui/lab'
import { creativeLetter } from 'app/util'
import ViewModuleIcon from '@material-ui/icons/ViewModule'
import ViewListIcon from '@material-ui/icons/ViewList'

export type Props = {
  creatives?: AdCreative[]
  selectedCreatives: AdCreative[]
  selectCreative: (creative: AdCreative) => void
  onSelectAll?: (visibleCreatives: AdCreative[]) => void
  onDeselectAll?: (visibleCreatives: AdCreative[]) => void
  onSelectLeadless?: (visibleCreatives: AdCreative[]) => void
  isHighlighted?: (creative: AdCreative) => boolean
  disableSelect?: (creative: AdCreative) => boolean
  /**
   * Why an ad may not be added, or undefined if it may. Unlike `disableSelect`
   * this is one-way: a withheld ad that is already selected stays clickable, so
   * it can be taken back off. Ads reach a draft campaign without ever passing
   * through this list, and the screen that reports them as the problem sends the
   * AE here to fix it.
   */
  withholdSelect?: (creative: AdCreative) => string | undefined
  /**
   * Take every attached withheld ad back off in one click. Only the campaign
   * picker supplies it: the ads arrive attached in bulk from sprint creation, and
   * clearing them one card at a time across ~50 ads is the whole complaint.
   */
  onRemoveWithheld?: (creatives: AdCreative[]) => void
  /**
   * The review surface, for repairing an ad rather than dropping it. Opens in a new
   * tab: this list sits in a modal over campaign edits that aren't saved yet.
   */
  reviewHref?: string
  maxSelectable?: number
  fetchNextPage?: () => void
  hasNextPage?: boolean
  isFetchingNextPage?: boolean
  isFetching?: boolean
  defaultViewMode?: 'grid' | 'list'
  /**
   * The list is sharing its width with something else, so the grid has to fit its
   * columns to the room it actually has rather than to the window.
   */
  compact?: boolean
  /**
   * Label the selected ads with their ad-set letter. Only the campaign picker, where
   * an ad's slot is what the letter names; elsewhere it is a letter about nothing.
   */
  showCreativeLetters?: boolean
}

export const AdCreativeList = ({
  creatives,
  selectedCreatives,
  selectCreative,
  onSelectAll,
  onDeselectAll,
  onSelectLeadless,
  isHighlighted,
  disableSelect,
  withholdSelect,
  onRemoveWithheld,
  reviewHref,
  maxSelectable,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  isFetching,
  defaultViewMode = 'grid',
  compact,
  showCreativeLetters = true,
}: Props) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncing, setDebouncing] = useState(true)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(defaultViewMode)
  const containerRef = useRef<HTMLDivElement>(null)
  const initialSelectedIds = useRef<Set<number> | null>(null)
  if (initialSelectedIds.current === null && selectedCreatives.length > 0) {
    initialSelectedIds.current = new Set(selectedCreatives.map(c => c.id))
  }

  useEffect(() => {
    setTimeout(() => setDebouncing(false), 500)
  }, [])

  // Auto-fetch next page when the filtered creative list is empty OR when the content is too
  // short to scroll (e.g. lead-gen filtering removed most creatives from the loaded pages).
  // Without this, the infinite-scroll listener never fires and the user gets stuck.
  useEffect(() => {
    if (!fetchNextPage || !hasNextPage || isFetchingNextPage) return
    if (!creatives || debouncing) return

    // Case 1: no creatives at all after filtering – fetch immediately
    if (creatives.length === 0) {
      fetchNextPage()
      return
    }

    // Case 2: some creatives but not enough to make the container scrollable
    if (!containerRef.current) return
    const scrollParent =
      containerRef.current.closest<HTMLElement>('.overflow-y-auto') ?? document.documentElement
    if (scrollParent.scrollHeight <= scrollParent.clientHeight) {
      fetchNextPage()
    }
  }, [creatives, fetchNextPage, hasNextPage, isFetchingNextPage, debouncing])

  useEffect(() => {
    if (!fetchNextPage || !hasNextPage || !containerRef.current) return
    const scrollParent =
      containerRef.current.closest<HTMLElement>('.overflow-y-auto') ?? document.documentElement
    const onScroll = () => {
      if (isFetchingNextPage) return
      if (scrollParent.scrollTop + scrollParent.clientHeight > scrollParent.scrollHeight - 200) {
        fetchNextPage()
      }
    }
    scrollParent.addEventListener('scroll', onScroll)
    return () => scrollParent.removeEventListener('scroll', onScroll)
  }, [hasNextPage, fetchNextPage, isFetchingNextPage, debouncing])

  const getButtonText = (creative: AdCreative) => {
    const index = selectedCreatives.findIndex(c => c.id === creative.id)
    return index >= 0 ? `Creative ${creativeLetter(index)}` : undefined
  }

  // Four bands, worst problem first: an ad that is withheld *and* attached is the one
  // thing on the screen that has to change, while one that is withheld and unattached
  // is neither eligible nor wanted and sinks to the bottom. On a 50-ad campaign that
  // is what makes a rejection findable — it used to sit wherever it fell inside the
  // attached block, which is the middle of the grid.
  //
  // Ranked on the selection as it was when the picker opened, like the ordering that
  // came before it: ranking live selection would fling a card across the grid on the
  // click that selected it, and take the rejected ones out from under the AE who just
  // removed them.
  const sortRank = (creative: AdCreative) => {
    const wasAttached = initialSelectedIds.current?.has(creative.id) ?? false
    if (withholdSelect?.(creative)) return wasAttached ? 0 : 3
    return wasAttached ? 1 : 2
  }

  const sortedCreatives = creatives
    ? (fuzzySearch(creatives, searchTerm) as AdCreative[]).sort((a, b) => sortRank(a) - sortRank(b))
    : []

  // Locked (e.g. published) and withheld (e.g. client-rejected) creatives are shown and counted
  // but excluded from every bulk-selection target — select all, deselect all, and select-leadless
  // all operate only over what's actually selectable, so they can never silently pull in a
  // creative the UI won't let someone add one at a time.
  const selectableCreatives = sortedCreatives.filter(
    c => !disableSelect?.(c) && !withholdSelect?.(c)
  )

  const selectedIds = new Set(selectedCreatives.map(c => c.id))

  // Only the attached ones: a withheld ad that was never picked has nothing to remove.
  const attachedWithheld = sortedCreatives.filter(
    creative => !!withholdSelect?.(creative) && selectedIds.has(creative.id)
  )

  // A withheld ad blocks the click only while it isn't attached: attached, the click is the
  // removal, which is the whole reason withholding isn't just `disableSelect`.
  const clickBlocked = (creative: AdCreative) =>
    !!disableSelect?.(creative) ||
    (!!withholdSelect?.(creative) && !selectedCreatives.some(c => c.id === creative.id))
  const visibleSelectedCount = selectableCreatives.filter(c => selectedIds.has(c.id)).length
  const unselectedVisibleCount = selectableCreatives.length - visibleSelectedCount
  const remainingSlots = (maxSelectable ?? Infinity) - selectedCreatives.length
  // Once the cap is hit, or every visible ad is already selected, there's nothing left to
  // add — offer to undo the visible selection instead of a dead-end disabled button.
  const canSelectMore = unselectedVisibleCount > 0 && remainingSlots > 0

  let selectAllLabel: string
  let selectAllDisabled: boolean
  let onSelectAllClick: () => void

  if (!canSelectMore && onDeselectAll && visibleSelectedCount > 0) {
    selectAllLabel = 'Deselect All'
    selectAllDisabled = false
    onSelectAllClick = () => onDeselectAll(selectableCreatives)
  } else {
    selectAllLabel =
      unselectedVisibleCount <= remainingSlots ? 'Select All' : `Select First ${remainingSlots}`
    selectAllDisabled = !onSelectAll || !canSelectMore
    onSelectAllClick = () => onSelectAll?.(selectableCreatives)
  }

  const leadlessCreatives = selectableCreatives.filter(c => !c.leadCardUsed)
  const unselectedLeadlessCount = leadlessCreatives.filter(c => !selectedIds.has(c.id)).length

  return (
    <>
      <div className="flex w-full space-x-4 p-4 pb-2 items-center justify-center">
        <input
          className="bg-white w-full rounded-md border px-4 py-3 max-w-6xl"
          type="text"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Filter Ads"
        />
        {(onSelectAll || onDeselectAll) && (
          <button
            type="button"
            disabled={selectAllDisabled}
            onClick={onSelectAllClick}
            className="flex-shrink-0 whitespace-nowrap rounded-md border px-4 py-3 font-semibold text-sm bg-white text-purple-700 border-purple-600 hover:bg-purple-50 disabled:opacity-40 disabled:hover:bg-white disabled:cursor-not-allowed"
          >
            {selectAllLabel}
          </button>
        )}
        {onSelectLeadless && (
          <button
            type="button"
            disabled={unselectedLeadlessCount === 0}
            onClick={() => onSelectLeadless(leadlessCreatives)}
            className="flex-shrink-0 whitespace-nowrap rounded-md border px-4 py-3 font-semibold text-sm bg-white text-purple-700 border-purple-600 hover:bg-purple-50 disabled:opacity-40 disabled:hover:bg-white disabled:cursor-not-allowed"
          >
            Select Lead-less
          </button>
        )}
        {onRemoveWithheld && attachedWithheld.length > 0 && (
          <button
            type="button"
            onClick={() => onRemoveWithheld(attachedWithheld)}
            className="flex-shrink-0 whitespace-nowrap rounded-md border px-4 py-3 font-semibold text-sm bg-white text-red-700 border-red-600 hover:bg-red-50"
          >
            Remove {attachedWithheld.length} Rejected
          </button>
        )}
        {reviewHref && (
          <a
            href={reviewHref}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 whitespace-nowrap rounded-md border px-4 py-3 font-semibold text-sm bg-white text-purple-700 border-purple-600 hover:bg-purple-50"
          >
            Edit Ads
          </a>
        )}
        <div className="flex rounded-md border overflow-hidden flex-shrink-0">
          <button
            type="button"
            aria-label="Grid view"
            onClick={() => setViewMode('grid')}
            className={`p-3 ${
              viewMode === 'grid'
                ? 'bg-purple-600 text-white'
                : 'bg-white text-gray-500 hover:bg-gray-50'
            }`}
          >
            <ViewModuleIcon fontSize="small" />
          </button>
          <button
            type="button"
            aria-label="List view"
            onClick={() => setViewMode('list')}
            className={`p-3 border-l ${
              viewMode === 'list'
                ? 'bg-purple-600 text-white'
                : 'bg-white text-gray-500 hover:bg-gray-50'
            }`}
          >
            <ViewListIcon fontSize="small" />
          </button>
        </div>
      </div>

      {creatives && creatives.length === 0 && !isFetching && (
        <div className="max-w-5xl mx-auto">
          <Alert severity="warning">
            No creatives? Make sure that your ads have all of the required info added. Also, check
            and see whether you have set the correct optimization in your campaign. If this is a
            Lead Gen campaign, you will only see creatives that have a lead card attached.
          </Alert>
        </div>
      )}

      {creatives && !debouncing ? (
        <div ref={containerRef} className="w-full">
          {viewMode === 'list' ? (
            <div className="max-w-4xl mx-auto w-full border rounded-lg divide-y overflow-hidden bg-white">
              {sortedCreatives.map(creative => (
                <RenderWhenVisible key={creative.id}>
                  <CreativeListRow
                    creative={creative}
                    onSelect={selectCreative}
                    selected={selectedIds.has(creative.id)}
                    trailing={
                      showCreativeLetters && (
                        <span className="text-sm font-semibold text-purple-600">
                          {getButtonText(creative)}
                        </span>
                      )
                    }
                    highlighted={isHighlighted?.(creative)}
                    disabled={clickBlocked(creative)}
                    withheldReason={withholdSelect?.(creative)}
                  />
                </RenderWhenVisible>
              ))}
            </div>
          ) : (
            // Sharing width means the column count has to come from the container, and
            // Tailwind's grid-cols-N can't: its breakpoints read the viewport, so a grid
            // beside a panel keeps rendering the columns the window would allow and the
            // fixed-width cards overflow. auto-fill fits as many 320px cards as actually
            // fit. The full-width case keeps its breakpoints — nothing shares its width.
            <div
              className="grid w-full justify-items-center gap-6 sm:grid-cols-3 3xl:grid-cols-4 4xl:grid-cols-5"
              style={
                compact
                  ? { gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))' }
                  : undefined
              }
            >
              {sortedCreatives.map(creative => (
                <RenderWhenVisible key={creative.id}>
                  <CreativeCard
                    tools={false}
                    creative={creative}
                    onSelect={selectCreative}
                    selected={selectedIds.has(creative.id)}
                    selectedText={showCreativeLetters ? getButtonText(creative) : undefined}
                    highlighted={isHighlighted?.(creative)}
                    disabled={clickBlocked(creative)}
                    withheldReason={withholdSelect?.(creative)}
                    pillDisplayed={'format'}
                  />
                </RenderWhenVisible>
              ))}
            </div>
          )}
        </div>
      ) : (
        <Loading />
      )}
    </>
  )
}
